/**
 * CompareView — Session 4A. Two/three columns, per 100g so the comparison is
 * valid. Facts, not verdicts: no ranking, no recommendation, no colours.
 */
import { useMemo, useState } from "react";
import { COMPARE_ROWS, GOALS, diffFacts, goalWinner, per100, productName } from "../../utils/compareFacts";

const T = {
  base: "#ECEAE6", surface: "#E4E2DE", ink: "#2A2825",
  ink2: "rgba(42,40,37,0.65)", ink3: "rgba(42,40,37,0.40)", line: "rgba(42,40,37,0.10)",
};
const HEAD = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
const BODY = { fontFamily: "'Barlow', system-ui, sans-serif" };

const fmt = (v, unit) => {
  if (v == null) return "—";
  const n = unit === "mg" || unit === "kcal" ? Math.floor(v + 0.5) : Math.floor(v * 10 + 0.5) / 10;
  return `${n}`;
};

export default function CompareView({ products, onAddAnother, onClose }) {
  const [goalKey, setGoalKey] = useState(null);
  const goal = GOALS.find((g) => g.key === goalKey) || null;
  const winner = useMemo(() => (goal ? goalWinner(products, goal) : null), [products, goal]);
  const facts = useMemo(() => diffFacts(products), [products]);

  const servingLine = useMemo(() => {
    const parts = products
      .map((c, i) => {
        const s = c?.serving || {};
        return s.size_value ? `${productName(c, i)} ${s.size_value}${s.size_unit || "g"}` : null;
      })
      .filter(Boolean);
    return parts.length >= 2 ? `Compared per 100g. Serving sizes differ: ${parts.join(", ")}.`
      : "Compared per 100g.";
  }, [products]);

  return (
    <div data-testid="compare-view"
         style={{ ...BODY, background: T.base, color: T.ink, borderRadius: 16, border: `1px solid ${T.line}`, padding: 20 }}>
      <div style={{ ...HEAD, fontSize: 24, fontWeight: 800 }}>Compare</div>

      {/* Goal filter — the user picks the ONE thing they care about */}
      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="goal-filter">
        {GOALS.map((g) => (
          <button key={g.key} type="button" data-testid={`goal-${g.key}`}
                  onClick={() => setGoalKey(goalKey === g.key ? null : g.key)}
                  style={{ ...BODY, height: 32, padding: "0 12px", borderRadius: 100, fontSize: 12, fontWeight: 600,
                           background: goalKey === g.key ? T.ink : "transparent",
                           color: goalKey === g.key ? T.base : T.ink, border: `1px solid ${T.line}` }}>
            {g.label}
          </button>
        ))}
      </div>
      {winner && (
        <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600 }} data-testid="goal-winner">
          {winner.line}
        </div>
      )}

      <table style={{ width: "100%", marginTop: 14, borderCollapse: "collapse", fontSize: 13 }} data-testid="compare-table">
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "6px 0", color: T.ink3, fontWeight: 500 }}>per 100g</th>
            {products.map((c, i) => (
              <th key={i} style={{ ...HEAD, textAlign: "right", padding: "6px 0", fontSize: 15, fontWeight: 700 }}>
                {productName(c, i)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map(([field, name, unit]) => {
            const highlighted = goal?.field === field;
            return (
              <tr key={field} data-testid={`compare-row-${field}`}
                  style={{ borderTop: `1px solid ${T.line}`,
                           background: highlighted ? T.surface : "transparent" }}>
                <td style={{ padding: "7px 4px", color: T.ink2, fontWeight: highlighted ? 700 : 400 }}>
                  {name}{unit !== "kcal" ? ` (${unit})` : ""}
                </td>
                {products.map((c, i) => (
                  <td key={i} style={{ padding: "7px 4px", textAlign: "right",
                                       fontWeight: highlighted && winner?.index === i ? 800 : 600 }}>
                    {fmt(per100(c)?.[field], unit)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: T.ink3, marginTop: 6 }} data-testid="compare-serving-line">
        {servingLine}
      </div>

      {facts.length > 0 && (
        <div style={{ marginTop: 12 }} data-testid="compare-facts">
          {facts.map((f) => (
            <div key={f} style={{ fontSize: 14, padding: "4px 0", borderBottom: `1px solid ${T.line}` }}>
              {f}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
        {products.length < 3 && onAddAnother && (
          <button type="button" data-testid="compare-add" onClick={onAddAnother}
                  style={{ ...BODY, height: 44, borderRadius: 100, background: T.ink, color: T.base, fontWeight: 600, fontSize: 14 }}>
            Compare a third · uses 1 scan
          </button>
        )}
        <button type="button" data-testid="compare-done" onClick={onClose}
                style={{ ...BODY, height: 44, borderRadius: 100, background: "transparent", color: T.ink,
                         border: `1px solid ${T.line}`, fontWeight: 600, fontSize: 14 }}>
          Done
        </button>
      </div>
    </div>
  );
}
